import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { getVendorLogo } from "../../organization/vendorLogoUtils";
import MapTemplateConfirmDialog, {
  MapConfirmDeviceRow,
  MappingStatus,
} from "../../organization/MapTemplateConfirmDialog";

/** Same gate as New Scan — kept local to avoid importing the heavy NewScanDrawer module. */
const isEndpointVendorName = (vendorName?: string | null): boolean => {
  const normalized = String(vendorName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (!normalized) return false;
  const endpoints = [
    "microsoft",
    "windows",
    "ubuntu",
    "debian",
    "redhat",
    "linux",
    "apple",
    "macos",
    "mac",
    "centos",
    "fedora",
    "suse",
    "oraclelinux",
  ];
  return endpoints.some(
    (name) => normalized === name || normalized.startsWith(name)
  );
};

const ENDPOINT_TEMPLATE_INFO_MESSAGE =
  "Config templates are not available for Windows or Linux devices.";

export type MapAssetTemplateDevice = {
  id: string;
  device_name?: string;
  host_name?: string;
  name?: string;
  host_ip_address?: string;
  ip_address?: string;
  vendor?: string;
  vendor_name?: string;
  model?: string;
  device_model_name?: string;
  os?: string;
  device_type?: string;
  type?: string;
};

type TemplateOption = {
  template_id: string;
  name: string;
  vendor_name: string;
  device_model_name: string;
  status_label?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Single-device mode (row kebab). Prefer `devices` for multi. */
  device?: MapAssetTemplateDevice | null;
  /** Multi-asset mode (Actions bulk). */
  devices?: MapAssetTemplateDevice[];
  isDarkMode?: boolean;
};

const norm = (v: any) => String(v ?? "").trim().toLowerCase();

const getDeviceVendor = (device: MapAssetTemplateDevice | null | undefined) =>
  String(device?.vendor || device?.vendor_name || "").trim();

const getDeviceModel = (device: MapAssetTemplateDevice | null | undefined) =>
  String(device?.model || device?.device_model_name || device?.os || "").trim();

const getDeviceLabel = (device: MapAssetTemplateDevice | null | undefined) =>
  device?.device_name ||
  device?.host_name ||
  device?.name ||
  device?.host_ip_address ||
  device?.ip_address ||
  device?.id ||
  "device";

const getDeviceIp = (device: MapAssetTemplateDevice | null | undefined) =>
  String(device?.host_ip_address || device?.ip_address || "").trim();

const isTemplateActive = (row: any): boolean => {
  const value = row?.active ?? row?.is_active;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const n = value.trim().toLowerCase();
    return n === "true" || n === "active" || n === "1" || n === "yes";
  }
  return Boolean(value);
};

const isTemplateSuccess = (row: any): boolean => {
  const label = String(row?.status_label ?? "").trim().toLowerCase();
  if (label === "success") return true;
  return row?.status === 3 || row?.status === "3";
};

/** Network on-prem devices eligible for master template mapping (same gate as New Scan). */
export const canMapTemplateToAsset = (asset: any): boolean => {
  if (!asset) return false;
  const type = norm(asset.type || asset.scan_type);
  if (type === "discovery" || type === "cloud") return false;
  if (norm(asset.device_type) === "endpoint") return false;
  const vendor = getDeviceVendor(asset);
  if (!vendor || isEndpointVendorName(vendor)) return false;
  if (!getDeviceModel(asset)) return false;
  return true;
};

const MapAssetTemplateDialog: React.FC<Props> = ({
  open,
  onClose,
  device = null,
  devices,
  isDarkMode = false,
}) => {
  const deviceList = useMemo(() => {
    if (devices && devices.length > 0) return devices;
    if (device) return [device];
    return [];
  }, [devices, device]);

  const isMulti = deviceList.length > 1;
  const primary = deviceList[0] || null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [currentMapping, setCurrentMapping] = useState<{
    template_id: string;
    template_name?: string;
  } | null>(null);
  const [mappingByDevice, setMappingByDevice] = useState<
    Record<
      string,
      { template_id: string; template_name?: string | null }
    >
  >({});
  const [templateNamesById, setTemplateNamesById] = useState<
    Record<string, string>
  >({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingConfirmRows, setPendingConfirmRows] = useState<
    MapConfirmDeviceRow[]
  >([]);

  const vendorName = getDeviceVendor(primary);
  const modelName = getDeviceModel(primary);
  const deviceIp = getDeviceIp(primary);
  const isEndpointVendor = isEndpointVendorName(vendorName);
  const vendorLogo = getVendorLogo(vendorName, isDarkMode);
  const deviceIdsKey = deviceList.map((d) => String(d.id || "")).join(",");

  const selectedTemplateName = useMemo(
    () =>
      templates.find((t) => t.template_id === selectedTemplateId)?.name ||
      templateNamesById[selectedTemplateId] ||
      selectedTemplateId,
    [templates, selectedTemplateId, templateNamesById]
  );

  const loadData = useCallback(async () => {
    if (!open || deviceList.length === 0 || isEndpointVendor) {
      setTemplates([]);
      setCurrentMapping(null);
      setSelectedTemplateId("");
      setMappingByDevice({});
      return;
    }

    setLoading(true);
    try {
      const [templatesRes, mappingsRes] = await Promise.all([
        axiosInstance.get(END_POINTS.MASTER_TEMPLATE_LIST, {
          params: { active: true },
        }),
        axiosInstance.get(END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING, {
          params: { active: true },
        }),
      ]);

      const rawTemplates =
        templatesRes?.data?.data?.templates ||
        templatesRes?.data?.data?.results ||
        templatesRes?.data?.data ||
        templatesRes?.data ||
        [];
      const templateList = Array.isArray(rawTemplates) ? rawTemplates : [];
      const vendorFilter = norm(vendorName);
      const modelFilter = norm(modelName);

      const nameById: Record<string, string> = {};
      templateList.forEach((t: any) => {
        const id = String(t.template_id || t.id || "");
        if (!id) return;
        nameById[id] = String(
          t.name || t.template_name || t.file_name || id
        );
      });
      setTemplateNamesById(nameById);

      const options: TemplateOption[] = templateList
        .filter((t: any) => isTemplateActive(t) && isTemplateSuccess(t))
        .filter((t: any) => !isEndpointVendorName(t.vendor_name))
        .filter((t: any) => {
          if (vendorFilter && norm(t.vendor_name) !== vendorFilter) return false;
          if (modelFilter && norm(t.device_model_name) !== modelFilter)
            return false;
          return true;
        })
        .map((t: any) => ({
          template_id: String(t.template_id || t.id || ""),
          name: String(t.name || t.template_name || t.file_name || "Template"),
          vendor_name: String(t.vendor_name || ""),
          device_model_name: String(t.device_model_name || ""),
          status_label: t.status_label,
        }))
        .filter((t: TemplateOption) => t.template_id);

      setTemplates(options);

      const rawMappings =
        mappingsRes?.data?.data?.mappings ||
        mappingsRes?.data?.data?.results ||
        mappingsRes?.data?.data ||
        mappingsRes?.data ||
        [];
      const mappings = Array.isArray(rawMappings) ? rawMappings : [];
      const idSet = new Set(deviceList.map((d) => String(d.id || "")));

      const byDevice: Record<
        string,
        { template_id: string; template_name?: string | null }
      > = {};
      mappings.forEach((m: any) => {
        if (m?.active === false) return;
        const deviceId = String(m.scan_device_id || m.device_id || "");
        const tplId = String(m.template_id || m.master_template_id || "");
        if (!deviceId || !tplId || !idSet.has(deviceId)) return;
        byDevice[deviceId] = {
          template_id: tplId,
          template_name:
            m.template_name || m.name || nameById[tplId] || null,
        };
      });
      setMappingByDevice(byDevice);

      if (!isMulti) {
        const deviceId = String(primary?.id || "");
        const activeForDevice = byDevice[deviceId];
        if (activeForDevice) {
          const matched = options.find(
            (t) => t.template_id === activeForDevice.template_id
          );
          setCurrentMapping({
            template_id: activeForDevice.template_id,
            template_name:
              matched?.name ||
              activeForDevice.template_name ||
              activeForDevice.template_id,
          });
          setSelectedTemplateId(activeForDevice.template_id || "");
        } else {
          setCurrentMapping(null);
          setSelectedTemplateId("");
        }
      } else {
        setCurrentMapping(null);
        setSelectedTemplateId("");
      }
    } catch (error: any) {
      console.error("Failed to load templates for asset mapping", error);
      toast.error(
        error?.response?.data?.message || "Failed to load config templates"
      );
      setTemplates([]);
      setCurrentMapping(null);
      setSelectedTemplateId("");
      setMappingByDevice({});
    } finally {
      setLoading(false);
    }
  }, [
    open,
    deviceList,
    vendorName,
    modelName,
    isEndpointVendor,
    isMulti,
    primary,
  ]);

  useEffect(() => {
    if (open) {
      setConfirmOpen(false);
      setPendingConfirmRows([]);
      loadData();
    }
  }, [open, loadData, deviceIdsKey]);

  const buildConfirmRows = useCallback((): MapConfirmDeviceRow[] => {
    return deviceList.map((d) => {
      const id = String(d.id || "");
      const mapped = mappingByDevice[id];
      let mapping_status: MappingStatus = "unmapped";
      if (mapped?.template_id) {
        mapping_status =
          mapped.template_id === selectedTemplateId
            ? "mapped"
            : "mapped_elsewhere";
      }
      return {
        scan_device_id: id,
        hostname: getDeviceLabel(d),
        mapping_status,
        mapped_template_id: mapped?.template_id || null,
        mapped_template_name: mapped?.template_name || null,
      };
    });
  }, [deviceList, mappingByDevice, selectedTemplateId]);

  const runMap = async (rows: MapConfirmDeviceRow[]) => {
    if (!selectedTemplateId || rows.length === 0) {
      toast.error("Select a template to map");
      return;
    }
    setSaving(true);
    try {
      const response = await axiosInstance.post(
        END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING,
        {
          template_id: selectedTemplateId,
          scan_device_ids: rows.map((r) => r.scan_device_id),
        }
      );
      if (response?.status === 200 || response?.data?.status_code === 200) {
        toast.success(
          rows.length > 1
            ? `Mapped ${rows.length} assets to template`
            : "Template mapped to device"
        );
        setConfirmOpen(false);
        setPendingConfirmRows([]);
        onClose();
      } else {
        toast.error(response?.data?.message || "Failed to map template");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to map template");
    } finally {
      setSaving(false);
    }
  };

  const handleMap = async () => {
    if (!selectedTemplateId) {
      toast.error("Select a template to map");
      return;
    }

    if (isMulti) {
      const rows = buildConfirmRows().filter(
        (r) => r.mapping_status !== "mapped"
      );
      if (rows.length === 0) {
        toast.info("All selected assets are already mapped to this template");
        return;
      }
      setPendingConfirmRows(rows);
      setConfirmOpen(true);
      return;
    }

    await runMap(buildConfirmRows());
  };

  const handleUnmap = async () => {
    if (isMulti || deviceList.length === 0) return;
    const deviceId = String(primary?.id || "");
    if (!deviceId) return;
    setSaving(true);
    try {
      const response = await axiosInstance.post(
        END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING,
        {
          action: "unmap",
          scan_device_ids: [deviceId],
        }
      );
      if (response?.status === 200 || response?.data?.status_code === 200) {
        toast.success("Template unmapped from device");
        onClose();
      } else {
        toast.error(response?.data?.message || "Failed to unmap template");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to unmap template");
    } finally {
      setSaving(false);
    }
  };

  const muted = isDarkMode ? "#9CA3AF" : "#667085";
  const textPrimary = isDarkMode ? "#F3F4F6" : "#101828";
  const softBg = isDarkMode ? "#141414" : "#F9FAFB";

  return (
    <>
      <Dialog
        open={open && !confirmOpen}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
          },
        }}
      >
        <Box sx={{ px: 2.5, pt: 2.25, pb: 1, position: "relative" }}>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "18px",
              color: textPrimary,
              pr: 4,
            }}
          >
            Map Config Template
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: muted,
              mt: 0.35,
            }}
          >
            {isMulti
              ? `Choose which template applies to ${deviceList.length} selected assets.`
              : "Choose which template applies to this device."}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "12px",
              border: `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`,
              bgcolor: isDarkMode ? "#141414" : "#FFFFFF",
            }}
          >
            {isMulti ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  {vendorLogo ? (
                    <img
                      src={vendorLogo.src}
                      alt={vendorLogo.alt || vendorName || "vendor"}
                      style={{
                        maxHeight: 40,
                        maxWidth: 100,
                        objectFit: "contain",
                        objectPosition: "left center",
                        flexShrink: 0,
                        ...(vendorLogo.style || {}),
                      }}
                    />
                  ) : null}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: textPrimary,
                    }}
                  >
                    {deviceList.length} assets selected
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0,
                    mb: 1.5,
                  }}
                >
                  {[
                    { label: "Vendor", value: vendorName || "-" },
                    { label: "OS", value: modelName || "-" },
                  ].map((item, index) => (
                    <Box
                      key={item.label}
                      sx={{
                        minWidth: 0,
                        pr: index < 1 ? 1.5 : 0,
                        pl: index > 0 ? 1.5 : 0,
                        borderLeft:
                          index > 0
                            ? `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`
                            : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: muted,
                          mb: 0.35,
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.value}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    maxHeight: 140,
                    overflowY: "auto",
                    borderTop: `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`,
                    pt: 1,
                  }}
                >
                  {deviceList.map((d) => (
                    <Box
                      key={String(d.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.6,
                      }}
                    >
                      <DnsOutlinedIcon
                        sx={{ fontSize: 16, color: muted, flexShrink: 0 }}
                      />
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={getDeviceLabel(d)}
                      >
                        {getDeviceLabel(d)}
                      </Typography>
                      {getDeviceIp(d) ? (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: muted,
                            ml: "auto",
                            flexShrink: 0,
                          }}
                        >
                          {getDeviceIp(d)}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.75,
                  }}
                >
                  {vendorLogo ? (
                    <img
                      src={vendorLogo.src}
                      alt={vendorLogo.alt || vendorName || "vendor"}
                      style={{
                        maxHeight: 40,
                        maxWidth: 100,
                        objectFit: "contain",
                        objectPosition: "left center",
                        flexShrink: 0,
                        ...(vendorLogo.style || {}),
                      }}
                    />
                  ) : null}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: textPrimary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={getDeviceLabel(primary)}
                  >
                    {getDeviceLabel(primary)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 0,
                  }}
                >
                  {[
                    { label: "Vendor", value: vendorName || "-" },
                    { label: "OS", value: modelName || "-" },
                    { label: "IP Address", value: deviceIp || "-" },
                  ].map((item, index) => (
                    <Box
                      key={item.label}
                      sx={{
                        minWidth: 0,
                        pr: index < 2 ? 1.5 : 0,
                        pl: index > 0 ? 1.5 : 0,
                        borderLeft:
                          index > 0
                            ? `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`
                            : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: muted,
                          mb: 0.35,
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.value}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>

          {isEndpointVendor ? (
            <Typography sx={{ fontSize: "13px", color: "#B42318" }}>
              {ENDPOINT_TEMPLATE_INFO_MESSAGE}
            </Typography>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={26} sx={{ color: "#3A2ADA" }} />
            </Box>
          ) : templates.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: muted, lineHeight: 1.5 }}>
              No active Success templates match this device. Upload or activate a
              matching template in Config Template.
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: muted,
                  mb: 0.75,
                  letterSpacing: "0.02em",
                }}
              >
                Select template
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={selectedTemplateId}
                  onChange={(e) =>
                    setSelectedTemplateId(String(e.target.value))
                  }
                  disabled={saving}
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: muted,
                          }}
                        >
                          Select template
                        </Typography>
                      );
                    }
                    const match = templates.find(
                      (t) => t.template_id === value
                    );
                    return match?.name || String(value);
                  }}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: softBg,
                    "& .MuiSelect-select": {
                      py: 1.25,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: textPrimary,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography sx={{ fontSize: "13px", color: muted }}>
                      Select template
                    </Typography>
                  </MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.template_id} value={t.template_id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!isMulti && currentMapping ? (
                <Box
                  sx={{
                    mt: 1.75,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "8px",
                    bgcolor: softBg,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{ fontSize: "11px", fontWeight: 600, color: muted }}
                    >
                      Currently mapped
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={
                        currentMapping.template_name ||
                        currentMapping.template_id
                      }
                    >
                      {currentMapping.template_name ||
                        currentMapping.template_id}
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    onClick={handleUnmap}
                    disabled={saving}
                    startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      flexShrink: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      minWidth: 0,
                      px: 1,
                      color: "#B42318",
                      "&.MuiButton-text:hover": {
                        backgroundColor: "rgba(180, 35, 24, 0.06)",
                        color: "#B42318",
                      },
                    }}
                  >
                    Unmap
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pt: 1,
            pb: 2,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              borderColor: isDarkMode ? "#4B5563" : "#D0D5DD",
              color: textPrimary,
              bgcolor: "transparent",
              "&.MuiButton-outlined:hover": {
                borderColor: isDarkMode ? "#6B7280" : "#98A2B3",
                backgroundColor: softBg,
                color: textPrimary,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMap}
            disabled={
              saving ||
              loading ||
              isEndpointVendor ||
              !selectedTemplateId ||
              templates.length === 0
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              bgcolor: "#3A2ADA",
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": { bgcolor: "#2E21B6", boxShadow: "none", color: "#FFFFFF" },
            }}
          >
            {saving && !confirmOpen ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : isMulti ? (
              `Map Config Template (${deviceList.length})`
            ) : (
              "Map Config Template"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <MapTemplateConfirmDialog
        open={confirmOpen}
        action="map"
        rows={pendingConfirmRows}
        templateName={selectedTemplateName}
        templateNamesById={templateNamesById}
        isDarkMode={isDarkMode}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setConfirmOpen(false);
          setPendingConfirmRows([]);
        }}
        onConfirm={() => runMap(pendingConfirmRows)}
      />
    </>
  );
};

export default MapAssetTemplateDialog;
